import { create } from 'zustand';
import type { AdminPreferencesDto, UpdateAdminPreferencesDto } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { useAuthStore } from './auth.store';

/**
 * The signed-in person's own choices (ADR-109): the overview's sections in
 * their order, and the store summary they get by email. Read once a session;
 * saving replaces it with what the API kept.
 */
interface PreferencesState {
  preferences: AdminPreferencesDto | null;
  /** Whose they are: someone else signing in on this tab reads their own. */
  forUser: string | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  load: () => Promise<AdminPreferencesDto | null>;
  save: (patch: UpdateAdminPreferencesDto) => Promise<boolean>;
  clearError: () => void;
}

export const usePreferencesStore = create<PreferencesState>((set, get) => ({
  preferences: null,
  forUser: null,
  loading: false,
  saving: false,
  error: null,

  async load() {
    const me = useAuthStore.getState().user?.id ?? null;
    if (get().preferences && get().forUser === me) return get().preferences;
    set({ loading: true, ...(get().forUser === me ? {} : { preferences: null }) });
    try {
      const preferences = await withSession(() => adminApi.me.preferences());
      set({ preferences, forUser: me, loading: false });
      return preferences;
    } catch {
      // The default overview stands in: a missing preference is not worth a banner.
      set({ loading: false });
      return null;
    }
  },

  async save(patch) {
    set({ saving: true, error: null });
    try {
      const preferences = await withSession(() => adminApi.me.updatePreferences(patch));
      set({ preferences, saving: false });
      return true;
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not save.') });
      return false;
    }
  },

  clearError() {
    set({ error: null });
  },
}));
